import React from 'react';
import type { PrintDataFields } from '../hooks/use-print-data';
import { BevaEstimateBlock } from './BevaEstimateBlock';
import { DiagnosticVisuel } from './DiagnosticVisuel';
import { RiskIndicator, mapRiskToEnglish, formatRiskLabel } from './RiskIndicator';

interface WebBlockProps {
  data: PrintDataFields;
}

/** Valeur Beva avec séparateurs français ; « — » quand elle n'est pas disponible. */
const formatBeva = (beva: PrintDataFields['beva']): string => {
  if (beva === '—' || !beva) return '—';
  const bevaNum = typeof beva === 'number' ? beva : parseFloat(String(beva));
  if (isNaN(bevaNum)) return `${beva} €`;
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(bevaNum)} €`;
};

/** Contenu du « Diagnostic visuel » : légende de risque, valeur estimée, silhouette et photos. */
export const DiagnosticVisuelWebBlock = ({ data }: WebBlockProps) => (
  <>
  <div className="py-2 mb-2">
    <BevaEstimateBlock beva={data.beva} bevaDetail={data.bevaDetail} formatBevaValue={() => formatBeva(data.beva)} />
  </div>
  <div className="py-4">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
      <div className="text-sm sm:text-base text-[#4E5155] font-semibold">Evaluation du risque</div>

      {/* Risk Table - Responsive */}
      <div className="overflow-x-auto">
        <table className="border border-gray-400 border-collapse min-w-full lg:min-w-0">
          <tbody>
            <tr>
              <td className="border border-gray-200 p-0">
                <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#10b981]" style={{ width: '100%' }} />
              </td>
              <td className="border border-gray-200 p-0">
                <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#facc15]" style={{ width: '100%' }} />
              </td>
              <td className="border border-gray-200 p-0">
                <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#ef4444]" style={{ width: '100%' }} />
              </td>
              <td className="border border-gray-200 p-0">
                <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#000000]" style={{ width: '100%' }} />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                Risque faible
              </td>
              <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                Risque modéré
              </td>
              <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                Risque élevé
              </td>
              <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                Risque imminent
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    {/* Tree Photos */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <DiagnosticVisuel formData={data} resolver={data.resolver} />
      {data.img1 ? (
        <div className="overflow-hidden border border-border">
          <img
            src={data.img1}
            alt="Vue de l'arbre 1"
            className="w-full  sm:h-64 lg:h-[20rem] object-cover transition-transform duration-300 hover:scale-125 cursor-zoom-in"
          />
        </div>
      ) : (
        <div className="w-full h-48 sm:h-64 lg:h-[20rem] bg-white flex items-center justify-center text-xs text-muted-foreground"></div>
      )}
      {data.img2 ? (
        <div className="overflow-hidden border border-border">
          <img
            src={data.img2}
            alt="Vue de l'arbre 2"
            className="w-full sm:h-64 lg:h-[20rem] object-cover transition-transform duration-300 hover:scale-125 cursor-zoom-in"
          />
        </div>
      ) : (
        <div className="w-full h-48 sm:h-64 lg:h-[20rem] bg-white flex items-center justify-center text-xs text-muted-foreground"></div>
      )}
      {data.img3 ? (
        <div className="overflow-hidden border border-border">
          <img
            src={data.img3}
            alt="Vue de l'arbre 3"
            className="w-full sm:h-64 lg:h-[20rem] object-cover transition-transform duration-300 hover:scale-125 cursor-zoom-in"
          />
        </div>
      ) : (
        <div className="w-full h-48 sm:h-64 lg:h-[20rem] bg-white flex items-center justify-center text-xs text-muted-foreground"></div>
      )}
    </div>
  </div>
  </>
);

/** Contenu du « Diagnostic sanitaire » : tableau par partie de l'arbre, état global et dates. */
export const DiagnosticSanitaireWebBlock = ({ data }: WebBlockProps) => (
  <>
  {/* Table responsive - Horizontal scroll on mobile */}
  <div className="overflow-x-auto mt-4">
    <table className="w-full min-w-[600px] text-xs border-collapse border border-border">
      <thead>
        <tr>
          <th className="border border-border p-2 text-left w-1/6"></th>
          <th className="border border-border p-2 text-left w-2/6 text-xs sm:text-sm text-[#4E5155]">Défauts mécaniques</th>
          <th className="border border-border p-2 text-left w-2/6 text-xs sm:text-sm text-[#4E5155]">Préconisations de travaux</th>
          <th className="border border-border p-2 text-left w-1/6 text-xs sm:text-sm text-[#4E5155]">Risque de rupture</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border border-border p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Système racinaire</td>
          <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.defautsMecaniques.collet}</td>
          <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.travaux_collet}</td>
          <td className="border border-border p-2 sm:p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.collet)}</span>
              <RiskIndicator level={mapRiskToEnglish(data.risque.collet)} />
            </div>
          </td>
        </tr>
        <tr>
          <td className="border border-border p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Tronc</td>
          <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.defautsMecaniques.tronc}</td>
          <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.travaux_tronc}</td>
          <td className="border border-border p-2 sm:p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.tronc)}</span>
              <RiskIndicator level={mapRiskToEnglish(data.risque.tronc)} />
            </div>
          </td>
        </tr>
        <tr>
          <td className="border border-border p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Houppier</td>
          <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.defautsMecaniques.houppier}</td>
          <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.travaux_houppier}</td>
          <td className="border border-border p-2 sm:p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.houppier)}</span>
              <RiskIndicator level={mapRiskToEnglish(data.risque.houppier)} />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  {/* État sanitaire global and Date prochaine visite */}
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
    <div className="border border-gray-200">
      <table className="w-full text-xs border-collapse">
        <tbody>
          <tr>
            <td className="border-b border-gray-200 p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Etat sanitaire global</td>
            <td className="border-b border-l border-gray-200 p-2 align-top">
              <div className="flex flex-col break-words whitespace-normal">
                <span className="p-2 text-xs sm:text-sm capitalize break-words">
                  {data.etatSanitaire.general}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Risque de rupture général</td>
            <td className="p-2 border-l">
              <div className="flex items-center gap-2 p-2 sm:p-3">
                <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.general)}</span>
                <RiskIndicator level={mapRiskToEnglish(data.risque.general)} />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="border border-gray-200">
      <table className="w-full text-xs border-collapse">
        <tbody>

          <tr>
            <td className="border-b border-gray-200 p-2 sm:p-3 font-bold text-xs sm:text-sm text-[#4E5155]">Date d'intervention</td>
            <td className="border-b border-l border-gray-200 p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.date_travaux}</td>
          </tr>
          <tr>
            <td className="border-b border-gray-200 p-2 sm:p-3 font-bold text-xs sm:text-sm text-[#4E5155]">Date de prochaine visite</td>
            <td className="border-b border-l border-gray-200 p-2 sm:p-3 text-xs sm:text-sm">
              {data.travaux.date_pro_visite}
            </td>
          </tr>
          <tr>
            <td className="p-2 sm:p-3 font-bold text-xs sm:text-sm text-[#4E5155]">DT/DICT requis</td>
            <td className="p-2 sm:p-3 border-l text-xs sm:text-sm">{data.dictRequired}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  </>
);

export const CommentairesWebBlock = ({ data }: WebBlockProps) => (
  <div className="mt-6 text-xs sm:text-sm">
    <span className="font-bold text-sm">Commentaires :</span>
    <div className="mt-2 text-muted-foreground break-words">{data.comments}</div>
  </div>
);
